import {
  Pen, Highlighter, Minus as LineIcon, ArrowUpRight, Square, Circle,
  Type, Stamp, Eraser, Crosshair, Undo2, Redo2, Save, Trash2, X,
} from 'lucide-react'
import type { StrokeKind } from './drawing/types'

export const TOOLBAR_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#0a0a0a'] as const
export const TOOLBAR_BRUSH_SIZES = [2, 4, 8] as const

interface Props {
  activeTool: StrokeKind | 'laser'
  activeColor: string
  brushSize: number
  canUndo: boolean
  canRedo: boolean
  onSelectTool: (t: StrokeKind | 'laser') => void
  onBrushSize: (size: number) => void
  onUndo: () => void
  onRedo: () => void
  onSave: () => void
  onClear: () => void
  onExit?: () => void // shown in overlay window, omitted in in-app preview
}

export function AnnotateToolbar({
  activeTool, activeColor, brushSize, canUndo, canRedo,
  onSelectTool, onBrushSize, onUndo, onRedo, onSave, onClear, onExit,
}: Props) {
  return (
    <div className="inline-flex items-center gap-1 bg-neutral-900/95 backdrop-blur-sm rounded-xl px-2 py-1.5 shadow-2xl border border-white/10">
      <ToolBtn icon={<Pen size={16}/>} title="Pen" active={activeTool === 'pen'} onClick={() => onSelectTool('pen')} />
      <ToolBtn icon={<Highlighter size={16}/>} title="Highlighter" active={activeTool === 'highlighter'} onClick={() => onSelectTool('highlighter')} />
      <ToolBtn icon={<LineIcon size={16}/>} title="Line" active={activeTool === 'line'} onClick={() => onSelectTool('line')} />
      <ToolBtn icon={<ArrowUpRight size={16}/>} title="Arrow" active={activeTool === 'arrow'} onClick={() => onSelectTool('arrow')} />
      <ToolBtn icon={<Square size={16}/>} title="Rectangle" active={activeTool === 'rect'} onClick={() => onSelectTool('rect')} />
      <ToolBtn icon={<Circle size={16}/>} title="Oval" active={activeTool === 'oval'} onClick={() => onSelectTool('oval')} />
      <ToolBtn icon={<Type size={16}/>} title="Text" active={activeTool === 'text'} onClick={() => onSelectTool('text')} />
      <ToolBtn icon={<Stamp size={16}/>} title="Stamp" active={activeTool === 'stamp'} onClick={() => onSelectTool('stamp')} />
      <ToolBtn icon={<Eraser size={16}/>} title="Eraser" active={activeTool === 'eraser'} onClick={() => onSelectTool('eraser')} />
      <ToolBtn icon={<Crosshair size={16}/>} title="Laser" active={activeTool === 'laser'} onClick={() => onSelectTool('laser')} />
      {/* OSPROD-205: the per-annotation colour picker was removed — the
          system auto-assigns each user a distinct colour (colourForUser /
          resolveUserColours in ScreenShareAnnotation), so a manual pick never
          affected the rendered stroke colour and only misled users. The brush
          swatches below still preview the user's (auto) colour. */}
      <Sep/>
      <div className="flex items-center gap-0.5">
        {TOOLBAR_BRUSH_SIZES.map(s => (
          <button key={s} title={`${s}px`} onClick={() => onBrushSize(s)}
            className={`w-7 h-7 rounded-md flex items-center justify-center ${brushSize === s ? 'bg-white/20' : 'hover:bg-white/10'}`}>
            <span className="rounded-full" style={{ width: s*1.5, height: s*1.5, backgroundColor: activeColor }}/>
          </button>
        ))}
      </div>
      <Sep/>
      <ToolBtn icon={<Undo2 size={16}/>} title="Undo" disabled={!canUndo} onClick={onUndo}/>
      <ToolBtn icon={<Redo2 size={16}/>} title="Redo" disabled={!canRedo} onClick={onRedo}/>
      <ToolBtn icon={<Save size={16}/>} title="Save PNG" onClick={onSave}/>
      <ToolBtn icon={<Trash2 size={16}/>} title="Clear all" onClick={onClear} className="text-red-400 hover:bg-red-500/20"/>
      {onExit && (<><Sep/><ToolBtn icon={<X size={16}/>} title="Exit Annotate" onClick={onExit} className="bg-red-500/80 hover:bg-red-500 text-white"/></>)}
    </div>
  )
}

function ToolBtn({ icon, title, active, disabled, onClick, className = '' }: {
  icon: React.ReactNode; title: string; active?: boolean; disabled?: boolean; onClick: () => void; className?: string
}) {
  return (
    <button title={title} onClick={onClick} disabled={disabled}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition
        ${active ? 'bg-emerald-500 text-neutral-900' : 'text-white/90 hover:bg-white/10'}
        ${disabled ? 'opacity-30 cursor-not-allowed' : ''}
        ${className}`}>
      {icon}
    </button>
  )
}

function Sep() { return <span className="w-px h-5 bg-white/15 mx-1"/> }
